import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, Info, AlertTriangle, CheckCircle, FileText, ArrowDownCircle, ArrowUpCircle, X, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useDarkMode } from '../DarkModeContext';
import { useAuth } from '../context/AuthContext';
import { useChatContextSafe } from '../context/ChatContext';

const NotificationCenter = () => {
    const [notifications, setNotifications] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();
    const { darkMode } = useDarkMode();
    const { user, isAuthenticated } = useAuth();

    // Chat context for unread chat messages (safe — returns defaults if no ChatProvider)
    const { totalUnread: chatUnread } = useChatContextSafe();

    const fetchNotifications = async () => {
        if (!isAuthenticated) return;

        try {
            setIsLoading(true);
            const response = await api.get('/api/wallets/notifications/platform');
            setNotifications(response.data || []);
        } catch (error) {
            console.error("Failed to fetch platform notifications", error);
            setNotifications([]);
        } finally {
            setIsLoading(false);
        }
    };

    // Poll for notifications every 30 seconds
    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        } else {
            setNotifications([]);
        }
    }, [isAuthenticated]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleMarkRead = async (id, e) => {
        e.stopPropagation();
        // Chat notification is virtual — clicking it navigates to chat
        if (id === 'chat-unread') {
            navigate('/platform/customer-chat');
            setIsOpen(false);
            return;
        }
        try {
            await api.post(`/api/wallets/notifications/${id}/read`);
            setNotifications(prev => prev.filter(n => n.id !== id));
        } catch (error) {
            console.error("Failed to mark read", error);
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await api.post('/api/wallets/notifications/platform/read-all');
            setNotifications([]);
        } catch (error) {
            console.error("Failed to mark all as read", error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (notification.link) {
            navigate(notification.link);
            setIsOpen(false);
        }
    };

    const getIcon = (type, category) => {
        if (category === 'chat') {
            return <MessageCircle className={`w-5 h-5 ${darkMode ? 'text-pink-400' : 'text-pink-500'}`} />;
        }
        if (category === 'submission') {
            return <FileText className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />;
        }
        if (category === 'deposit') {
            return <ArrowDownCircle className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />;
        }
        if (category === 'withdrawal') {
            return <ArrowUpCircle className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />;
        }
        if (category === 'termsheet') {
            return <CheckCircle className={`w-5 h-5 ${darkMode ? 'text-purple-400' : 'text-purple-500'}`} />;
        }
        switch (type) {
            case 'SUCCESS': return <CheckCircle className={`w-5 h-5 ${darkMode ? 'text-green-400' : 'text-green-500'}`} />;
            case 'WARNING': return <AlertTriangle className={`w-5 h-5 ${darkMode ? 'text-amber-400' : 'text-amber-500'}`} />;
            case 'ERROR': return <X className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-500'}`} />;
            default: return <Info className={`w-5 h-5 ${darkMode ? 'text-blue-400' : 'text-blue-500'}`} />;
        }
    };

    const formatTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const now = new Date();
        const diff = now - date;

        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return date.toLocaleDateString();
    };

    if (!isAuthenticated) return null;

    // Build combined notification list: chat unread + platform notifications
    const allNotifications = [];

    // Add chat unread as a virtual notification at the top
    if (chatUnread > 0) {
        allNotifications.push({
            id: 'chat-unread',
            title: `${chatUnread} unread chat message${chatUnread > 1 ? 's' : ''}`,
            message: 'Click to open Customer Support Chat',
            type: 'INFO',
            category: 'chat',
            link: '/platform/customer-chat',
            created_at: new Date().toISOString(),
            _isChatNotification: true
        });
    }

    // Add regular platform notifications
    allNotifications.push(...notifications);

    const totalBadgeCount = notifications.length + (chatUnread > 0 ? 1 : 0);

    return (
        <div className="relative" ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`relative p-2 rounded-lg transition-colors ${
                    darkMode
                        ? 'text-gray-400 hover:text-white hover:bg-slate-700'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
            >
                <Bell className="w-6 h-6" />
                {totalBadgeCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1 text-xs font-bold text-white bg-red-500 rounded-full animate-pulse">
                        {totalBadgeCount > 99 ? '99+' : totalBadgeCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className={`origin-top-right absolute right-0 mt-2 w-80 sm:w-96 rounded-xl shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden ${
                    darkMode ? 'bg-slate-800 ring-slate-700' : 'bg-white'
                }`}>
                    {/* Header */}
                    <div className={`px-4 py-3 border-b flex items-center justify-between ${
                        darkMode ? 'border-slate-700' : 'border-gray-100'
                    }`}>
                        <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Platform Notifications
                        </h3>
                        {notifications.length > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                                    darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-700'
                                }`}
                            >
                                <CheckCheck className="w-4 h-4" />
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {isLoading && allNotifications.length === 0 ? (
                            <div className={`px-4 py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Loading...
                            </div>
                        ) : allNotifications.length === 0 ? (
                            <div className={`px-4 py-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                <Bell className="w-12 h-12 mx-auto mb-3 opacity-30" />
                                <p className="text-sm font-medium">No new notifications</p>
                                <p className="text-xs mt-1 opacity-70">You're all caught up!</p>
                            </div>
                        ) : (
                            allNotifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    onClick={() => handleNotificationClick(notification)}
                                    className={`px-4 py-3 border-b last:border-0 cursor-pointer transition-colors ${
                                        notification._isChatNotification
                                            ? darkMode
                                                ? 'border-slate-700 bg-pink-900/20 hover:bg-pink-900/30'
                                                : 'border-gray-100 bg-pink-50 hover:bg-pink-100'
                                            : darkMode
                                                ? 'border-slate-700 hover:bg-slate-700/50'
                                                : 'border-gray-100 hover:bg-gray-50'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-0.5">
                                            {getIcon(notification.type, notification.category)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm font-medium truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                                {notification.title}
                                            </p>
                                            <p className={`mt-0.5 text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {notification.message}
                                            </p>
                                            {!notification._isChatNotification && (
                                                <p className={`mt-1 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                                    {formatTime(notification.created_at)}
                                                </p>
                                            )}
                                        </div>
                                        {!notification._isChatNotification && (
                                            <button
                                                onClick={(e) => handleMarkRead(notification.id, e)}
                                                className={`flex-shrink-0 p-1 rounded transition-colors ${
                                                    darkMode
                                                        ? 'text-gray-500 hover:text-gray-300 hover:bg-slate-600'
                                                        : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                                                }`}
                                                title="Mark as read"
                                            >
                                                <Check className="w-4 h-4" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
